import cx from 'classnames';
import { DeviceStatusBadge } from '../DeviceStatusBadge';
import type { DeviceListGridProps } from './types';
import { getStatus } from '../../utils/getStatus';

/**
 * DeviceListGrid - Card grid view with inline content (no expansion)
 */
export const DeviceListGrid: React.FC<DeviceListGridProps> = ({
  app,
  devices,
  onClick,
  children,
  actions,
  t,
}) => {
  // Grid view doesn't use expansion - metrics are always displayed inline
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
      data-component="DeviceListGrid"
    >
      {devices.map((device) => {
        const handleClick = () => {
          if (onClick) {
            onClick(device.id);
          }
        };

        return (
          <div key={device.id} className="flex flex-col h-full">
            {/* Card */}
            <div
              className={cx(
                'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4',
                'shadow-sm hover:shadow-md transition-shadow',
                'flex flex-col h-full',
                {
                  'cursor-pointer': onClick,
                  'opacity-60': getStatus(app, device.status) === 'offline',
                }
              )}
              onClick={handleClick}
              role={onClick ? 'button' : undefined}
              tabIndex={onClick ? 0 : undefined}
              onKeyDown={(e) => {
                // Leave keys aimed at nested controls to those controls.
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick();
                }
              }}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground truncate">
                    {device.deviceName}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {device.serialNumber}
                  </p>
                </div>
                <DeviceStatusBadge app={app} status={device.status} size="sm" t={t} />
              </div>

              {/* Custom Fields */}
              <div className="mb-3 text-sm text-foreground-secondary space-y-1">
                <div className="truncate">
                  {device.field1 ? device.field1 : <br />}
                </div>
                <div className="truncate">
                  {device.field2 ? device.field2 : <br />}
                </div>
              </div>

              {/* Last Seen */}
              {device.lastSeen && (
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{t('Status for')}</span>
                  <span title={device.lastSeenAbsolute} className="cursor-help">
                    {device.lastSeen}
                  </span>
                </div>
              )}

              {/* Actions (e.g. monitor button) */}
              {actions && (
                <div
                  className="mt-3"
                  onClick={(e) => e.stopPropagation()}
                  role="none"
                >
                  {actions(device)}
                </div>
              )}

              {/* Inline Content (Metrics) - always at bottom */}
              {children && (
                <div className="mt-auto pt-3 border-t border-gray-200 dark:border-gray-700">
                  {children(device)}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
